// src/components/pages/Pessoa/modules/usePessoaTelefoneModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';

function createTelefoneDraft(pessoaId = null) {
    return {
        pessoaId: pessoaId || null,
        uuid: '',
        tipo: 'Pessoal',
        numero: '',
        principal: false,
        zap: false,
        telegram: false,
        ativo: false,
        funcao: 'Adicionar',
    };
}

export function usePessoaTelefoneModule({ pessoaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createTelefoneDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const resetDraft = useCallback(() => {
        setDraft(createTelefoneDraft(pessoaId));
        setEditingIndex(null);
    }, [pessoaId]);

    const handleEdit = useCallback(
        (row, index) => {
            setDraft({
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                tipo: row.tipo || 'Pessoal',
                numero: row.numero || '',
                principal: !!row.principal,
                zap: !!row.zap,
                telegram: !!row.telegram,
                ativo: !!row.ativo,
                funcao: 'Editar',
            });
            setEditingIndex(index);
        },
        [pessoaId]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                tipo: row.tipo || 'Pessoal',
                numero: row.numero || '',
                principal: !!row.principal,
                zap: !!row.zap,
                telegram: !!row.telegram,
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Telefone removido da lista.', 'success');
                if (editingIndex === index) resetDraft();
                return;
            }

            try {
                const result = await PessoaAPI.telefones(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) resetDraft();
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir telefone.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.numero?.trim()) {
            setFlashMessage?.('Digite o telefone.', 'warning');
            return;
        }

        const payload = {
            ...draft,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                              }
                            : item
                    )
                );
                setFlashMessage?.('Telefone atualizado na lista.', 'success');
            } else {
                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        uuid: payload.uuid || `draft-tel-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Telefone adicionado na lista.', 'success');
            }

            resetDraft();
            return;
        }

        try {
            const result = await PessoaAPI.telefones(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');
            resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar telefone.', 'error');
        }
    }, [draft, pessoaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                id: 'telefone',
                header: 'Telefones da pessoa',
                columns: [
                    {
                        accessorKey: 'numero',
                        header: 'Número',
                        size: 180,
                    },
                    {
                        accessorKey: 'tipo',
                        header: 'Tipo',
                        size: 120,
                    },
                    {
                        accessorKey: 'principal',
                        header: 'Principal',
                        size: 90,
                        Cell: ({ row }) => (row.original.principal ? 'Sim' : 'Não'),
                    },
                    {
                        accessorKey: 'zap',
                        header: 'WhatsApp',
                        size: 90,
                        Cell: ({ row }) => (row.original.zap ? 'Sim' : 'Não'),
                    },
                    {
                        accessorKey: 'telegram',
                        header: 'Telegram',
                        size: 90,
                        Cell: ({ row }) => (row.original.telegram ? 'Sim' : 'Não'),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        size: 80,
                        Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    return {
        state: {
            items: rows || [],
            rowCount: rows?.length || 0,
            isLoading: false,
            isRefetching: false,
            isError: false,
            novoTelefone: draft,
        },
        actions: {
            setNovoTelefone: setDraft,
            limpar: resetDraft,
            submit: handleSave,
            handleEditarOuDeletar: (row, index, funcao) => {
                if (String(funcao).toLowerCase() === 'editar') {
                    handleEdit(row, index);
                    return;
                }
                handleDelete(row, index);
            },
            change: (e) => {
                const { name, value, checked, type, options, selectedIndex } = e.target;
                const field = name.split('.')[1];

                setDraft((prev) => ({
                    ...prev,
                    [field]: field === 'tipo' ? options?.[selectedIndex]?.text || value : type === 'checkbox' ? checked : value,
                }));
            },
            focus: () => {},
            blur: () => {},
        },
        columns,
    };
}
